import React, { useState } from 'react';
import clsx from 'clsx';
import { SheetLabel, UtSheet } from './UtSheet';
import { selectionSlots, type SlotSegment } from './sheetMaths';

interface Props {
  open: boolean;
  onClose: () => void;
  matchNumber?: number | string;
  maxPlayers: number;
  randomSlots: number;
  /** Priority tokens in use so far. */
  tokenCount: number;
}

const SEGMENT: Record<SlotSegment['key'], { title: string; body: string; className: string }> = {
  token: {
    title: 'Priority tokens',
    body: "Guaranteed places, taken out of the merit places (never the random ones). If you would have got in on XP anyway, your token isn't used up.",
    className: 'bg-ut-acc text-ut-acc-ink',
  },
  merit: {
    title: 'Merit, by XP',
    body: 'Highest XP fills the rest. Exact ties go to WhatsApp members, then longer streak, more caps, and who registered first.',
    className: 'bg-ut-ok text-[#04140c]',
  },
  random: {
    title: 'Random draw',
    body: 'A weighted draw for the last places, WhatsApp members first. Every reserve appearance in a row adds to your odds.',
    className: 'bg-[#c9a6ff] text-[#1a0b33]',
  },
};

// Checked against select_players_for_game / playerSelection.ts, use_shield_token and the XP v2 rules.
const FAQS: { q: string; a: string[] }[] = [
  {
    q: 'Can I register and use a shield?',
    a: [
      'Not both. While registration is open you can switch: using a shield cancels your registration. Once it closes, your choice is final.',
      'Selected: you play and your streak continues.',
      'Reserve: your attendance streak resets, but you earn reserve XP.',
      'Shield: your streak is protected and eases down when you return.',
    ],
  },
  {
    q: 'How do reserves work?',
    a: [
      "Anyone not selected goes on the reserve list, WhatsApp members first, then by XP.",
      'Reserves earn +5 XP for the game.',
      'If someone drops out, the place is offered to reserves in that order.',
    ],
  },
  {
    q: 'How do I improve my random odds?',
    a: [
      'Everyone starts with 1 point. Each reserve appearance in a row adds 1.',
      'Three reserves in a row means 4 points.',
      'WhatsApp members are drawn before non-members.',
    ],
  },
  {
    q: 'I used a priority token last week. Does that matter?',
    a: ['Yes. If you used a token in the previous game you go to the back of the merit list, and you are drawn last in the random draw, if at all.'],
  },
  {
    q: "What if I'm picked and don't turn up?",
    a: ["That's a no-show. It costs you XP, and you go to the back of the queue for the next 3 games. If you can't make it, drop out instead."],
  },
  {
    q: 'What if I have unpaid games?',
    a: ['Unpaid games cut your XP, which moves you down the merit list. Pay promptly to keep your place.'],
  },
];

/** "How selection works" sheet: the slot split for this game, the three stages, and an FAQ accordion. */
export const UtSelectionExplainer: React.FC<Props> = ({ open, onClose, matchNumber, maxPlayers, randomSlots, tokenCount }) => {
  const [faq, setFaq] = useState(0);
  const segments = selectionSlots(maxPlayers, randomSlots, tokenCount);
  const shown = segments.filter((s) => s.slots > 0);

  return (
    <UtSheet
      open={open}
      onClose={onClose}
      eyebrow={matchNumber ? `Match #${matchNumber}` : undefined}
      title="How selection works"
      cancelLabel="Got it"
    >
      <div className="flex flex-col gap-2 rounded-xl bg-ut-card p-3.5">
        <SheetLabel>How the {maxPlayers} places fill, in order</SheetLabel>
        <div className="flex h-[34px] gap-0.5 overflow-hidden rounded-lg">
          {shown.map((s) => (
            <span
              key={s.key}
              style={{ flex: s.slots }}
              className={clsx('flex items-center justify-center font-ut-display text-sm font-extrabold', SEGMENT[s.key].className)}
            >
              {s.slots}
            </span>
          ))}
        </div>
        {segments.map((s, i) => (
          <div key={s.key} className="grid grid-cols-[22px_1fr] items-baseline gap-2.5 pt-1.5">
            <span
              className={clsx(
                'flex h-[22px] w-[22px] items-center justify-center rounded-md font-ut-display text-[13px] font-extrabold',
                SEGMENT[s.key].className,
              )}
            >
              {i + 1}
            </span>
            <div className="flex flex-col gap-0.5">
              <b className="text-sm">
                {SEGMENT[s.key].title}
                <span className="font-normal text-ut-mut">
                  {' '}
                  · {s.slots} {s.slots === 1 ? 'place' : 'places'}
                  {s.key === 'token' ? ' so far' : ''}
                </span>
              </b>
              <span className="text-[13px] leading-[1.45] text-ut-mut">{SEGMENT[s.key].body}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col overflow-hidden rounded-xl bg-ut-card">
        {FAQS.map((f, i) => {
          const isOpen = faq === i;
          return (
            <div key={f.q} className="flex flex-col border-b border-ut-line last:border-b-0">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setFaq(isOpen ? -1 : i)}
                className="flex items-center justify-between gap-2.5 px-3.5 py-[13px] text-left"
              >
                <b className="text-sm">{f.q}</b>
                <span className="font-bold text-ut-mut">{isOpen ? '−' : '+'}</span>
              </button>
              {isOpen && (
                <div className="flex flex-col gap-1.5 px-3.5 pb-3.5">
                  {f.a.map((line) => (
                    <span key={line} className="border-l-2 border-ut-line pl-3 text-[13px] leading-normal text-ut-mut">
                      {line}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </UtSheet>
  );
};
